import type { Camera, Color, Intersection, ToneMapping } from 'three'
import { ACESFilmicToneMapping, AmbientLight, Clock, CubeTextureLoader, DefaultLoadingManager, Euler, Fog, FogExp2, Group, PCFShadowMap, PCFSoftShadowMap, PMREMGenerator, PerspectiveCamera, Raycaster, Scene as TScene, Texture, Vector2, Vector3, WebGLRenderTarget, WebGLRenderer } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

import * as TWEEN from '@tweenjs/tween.js'
import type { EffectPass } from 'postprocessing'
import type { ColorRepresentation } from '../../type'
import { FogEnum } from '../../type'
import { InteractionManager, use } from '../../export'
import type { CssRendererType } from '../cssRenderer'
import Cssrenderer from '../cssRenderer'

// import globalControl from '../global/globalControl'
import { SceneContext } from '../global/SceneContext'
import { EControlMode, UniversalControls } from '../control/UniversalControls'
import type { ControlsType } from '../control/UniversalControls'
import type { ISpawnPoint } from '../control/CharacterControls'
import PostProcess from './postProcess'
import { SceneEventName, registerEvents, removeCurrentEvent } from './events'
import { EnvManager } from './envManager'

interface SceneOptions {
  /** renderer options */
  rendererOps?: {
    antialias?: boolean
    logarithmicDepthBuffer?: boolean
    alpha?: boolean
    shadowMap?: boolean // 是否开启阴影
    toneMapping?: ToneMapping
    toneMappingExposure?: number
    size?: {
      width: number
      height: number
    }
  }

  /** window reset */
  reset?: boolean

  fog?: FogEnum

  /** default camera options */
  defCameraOps?: {
    position?: Vector3
    fov?: number
    aspect?: number
    near?: number
    far?: number
  }

  /** default ambient light options */
  defAmbientLightOps?: {
    color?: Color
    intensity?: number
  }

  /** 默认射线检测配置 */
  defRaycasterOps?: {
    recursive?: boolean
  }

  /** controls */
  orbitControls?: {
    use?: boolean
    mode?: {
      type: EControlMode
    }
    characterModelUrl?: string
    spawnPoint?: ISpawnPoint
    limit?: {
      pitch?: [number, number]
      yaw?: [number, number]
      distance?: [number, number]
    }
    target?: [number, number, number]
  }

  useBvh?: boolean

  /** 是否需要默认环境光 */
  ambientLight?: boolean

  /** on demand render scene, 按需渲染 */
  onDemand?: boolean

  /** 是否开启css2d渲染 */
  css2DRenderer?: boolean

  /** 是否开启css3d渲染 */
  css3DRenderer?: boolean

  /** 是否启用后期效果 */
  postEffects?: boolean

  /** 是否开启裁剪 */
  cutout?: boolean

  /** 场景背景相关 */
  background?: {
    imgs?: Tuple<string>
    color?: Color
    panorama?: string
    alpha?: number
  }

  enableEvents?: boolean

  /** 是否开启后期效果 */
  postProcess?: boolean

  /** 允许传入自定义事件总线 */
  emitter?: any
}

type Tuple<TItem> = [TItem, ...TItem[]] & { length: 6 }
interface CutoutAreaType {
  x: number
  y: number
  width: number
  height: number
}

class SceneControl {
  public readonly context: SceneContext
  private opts: SceneOptions = {}
  private container!: HTMLElement
  private _postProcess = false
  private postProcessManager: PostProcess | null = null
  private interactionManager: InteractionManager | null = null

  cutout = false
  scene: TScene
  ambientLight!: AmbientLight
  camera!: PerspectiveCamera
  renderer!: WebGLRenderer
  cssRenderer: Cssrenderer | null = null
  css3DRenderer: Cssrenderer | null = null
  controls!: ControlsType
  universalControls!: UniversalControls
  domElement!: HTMLElement

  fog: Fog | FogExp2 | null = null

  /** 裁剪相关 */
  cutoutCamera: PerspectiveCamera | null = null
  cutoutBackground: ColorRepresentation | null = null
  cutoutBackgroundAlpha = 1
  cutoutArea: CutoutAreaType = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  }

  /** 雾相关 */
  fogColor: ColorRepresentation = 0xCCCCCC
  fogNear = 1
  fogFar = 100
  fogDensity = 0.00025

  intersectObjects: Intersection[] = []

  private clock = new Clock()
  private perspectiveMap = new Map<Camera, WebGLRenderTarget>()

  public get postProcess() {
    return this._postProcess
  }

  public set postProcess(value: boolean) {
    this._postProcess = value

    if (value)
      this.postProcessManager = new PostProcess(this.scene, this.camera, this.renderer, this)

    else
      this.postProcessManager?.dispose()
  }

  constructor(opts?: SceneOptions) {
    this.opts = opts ?? {}
    this.context = new SceneContext(this.opts.emitter)
    this.cutout = this.opts.cutout ?? false

    this.scene = new TScene()

    // 拦截 scene.add 和 scene.remove，确保直接操作 scene 也能触发事件管理
    const originalAdd = this.scene.add.bind(this.scene)
    this.scene.add = (...objects: any[]) => {
      const result = originalAdd(...objects)
      objects.forEach((obj) => {
        this.handleAdd(obj)
      })
      return result
    }

    const originalRemove = this.scene.remove.bind(this.scene)
    this.scene.remove = (...objects: any[]) => {
      const result = originalRemove(...objects)
      objects.forEach((obj) => {
        this.handleRemove(obj)
      })
      return result
    }

    registerEvents(this.context)

    this.context.addScene(this.scene)
    this.context.setId(this.scene.uuid)

    const { useBvh, enableEvents, postEffects } = this.opts

    this.context.storeManagement.set('useBvh', useBvh ?? false)
    this.context.storeManagement.set('enableEvents', enableEvents ?? true)

    this.postProcess = postEffects ?? false
    this.defaultInit()
    this.registerLoadingEvents()
  }

  /**
   * enable events
   * @param flag
   */
  public enableEvents(flag: boolean) {
    this.context.storeManagement.set('enableEvents', flag ?? true)
  }

  /**
 * todo: 待优化
 * init default scene components
 */
  private defaultInit() {
    const camera = this.initDefaultPerspectiveCamera()
    this.camera = camera

    const cameraGroup = new Group()
    cameraGroup.add(camera)
    this.scene!.add(cameraGroup)

    camera.rotateX = (angle: number) => {
      cameraGroup.rotateX(angle)
      return camera
    }

    camera.rotateY = (angle: number) => {
      cameraGroup.rotateY(angle)
      return camera
    }

    camera.rotateZ = (angle: number) => {
      cameraGroup.rotateZ(angle)
      return camera
    }

    // @ts-ignore
    camera.rotationParent = cameraGroup.rotation

    this.context.setCamera(camera)

    if (this.opts.fog) {
      const fog = this.initFog()
      this.scene!.fog = fog
      this.fog = fog
    }

    if (this.opts.css2DRenderer)
      this.cssRenderer = this.initCssRenderer()

    if (this.opts.css3DRenderer)
      this.css3DRenderer = this.initCssRenderer('normal')

    if (this.opts.ambientLight) {
      const ambientLight = this.initAmbientLight()
      this.ambientLight = ambientLight
      this.scene!.add(ambientLight)
    }

    if (this.opts.background?.imgs)
      this.initSkyBox(this.opts.background?.imgs)
  }

  /**
   * set main camera
   * @param camera
   */
  public setMainCamera(camera: PerspectiveCamera) {
    this.camera = camera

    const cameraGroup = new Group()
    cameraGroup.add(camera)
    this.scene!.add(cameraGroup)

    camera.rotateX = (angle: number) => {
      cameraGroup.rotateX(angle)
      return camera
    }

    camera.rotateY = (angle: number) => {
      cameraGroup.rotateY(angle)
      return camera
    }

    camera.rotateZ = (angle: number) => {
      cameraGroup.rotateZ(angle)
      return camera
    }

    // @ts-ignore
    camera.rotationParent = cameraGroup.rotation

    this.context.setCamera(camera)

    if (this.controls) {
      this.controls.dispose()
      this.controls = new OrbitControls(this.camera, this.domElement!)

      this.context.setControls(this.controls)

      this.controls.addEventListener('start', () => {
        this.context.storeManagement.set('enableEvents', false)
      })

      this.controls.addEventListener('end', () => {
        this.context.storeManagement.set('enableEvents', true)
      })
    }
  }

  /**
   * init fog
   * @returns
   */
  public initFog() {
    const fogType = this.opts.fog
    if (fogType === FogEnum.Fog)
      return new Fog(this.fogColor, this.fogNear, this.fogFar)

    if (fogType === FogEnum.FogExp2)
      return new FogExp2(this.fogColor, this.fogDensity)

    return null
  }

  /**
   * init scene
   * @param camera
   * @param renderer
   */
  public resetScene(camera: PerspectiveCamera, renderer: WebGLRenderer) {
    const rendererOps = this.opts.rendererOps || {}
    const w = rendererOps.size?.width ?? this.container.offsetWidth
    const h = rendererOps.size?.height ?? this.container.offsetHeight
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    renderer.setSize(w, h)
    renderer.setViewport(0, 0, w, h)
    renderer.setPixelRatio(window.devicePixelRatio)
    // 同步 CSS 渲染层尺寸，避免视图缩放后面板位置与画布不同步
    this.cssRenderer && this.cssRenderer.setSize(w, h)
    this.css3DRenderer && this.css3DRenderer.setSize(w, h)
  }

  /**
   * init renderer
   */
  private initRenderer() {
    const rendererOps = this.opts.rendererOps || {}

    const renderer = new WebGLRenderer({
      antialias: rendererOps.antialias ?? true,
      logarithmicDepthBuffer: rendererOps.logarithmicDepthBuffer ?? true,
      alpha: rendererOps.alpha ?? true,
    })

    this.cutMain(renderer)

    renderer.shadowMap.enabled = rendererOps.shadowMap ?? false
    rendererOps.shadowMap && (renderer.shadowMap.type = PCFShadowMap)
    renderer.toneMapping = (rendererOps.toneMapping ?? ACESFilmicToneMapping) as ToneMapping
    renderer.toneMappingExposure = 0.6
    renderer.setPixelRatio(window.devicePixelRatio)

    return renderer
  }

  /**
   * main view cut
   * @param renderer
   */
  public cutMain(renderer: WebGLRenderer) {
    // 使用当前渲染器尺寸，确保与相机/画布/CSS 层一致
    const size = new Vector2()
    renderer.getSize(size)
    const w = size.x
    const h = size.y

    renderer.setClearColor(this.opts.background?.color || 0x000000, this.opts.background?.alpha || 1)
    // renderer.setSize(w, h)
    renderer.setScissor(0, 0, w, h)

    if (this.context.frameComposerFrameRender.size > 0)
      this.context.frameComposerFrameRender.forEach((cb) => { cb() })
    else
      renderer.render(this.scene!, this.camera!)
  }

  /**
   * mini view cut
   * @param renderer
   */
  private cutSub(renderer: WebGLRenderer) {
    const size = new Vector2()
    renderer.getSize(size)
    const w = size.x
    const h = size.y

    renderer.setScissor(this.cutoutArea.x, h - this.cutoutArea.height - this.cutoutArea.y, this.cutoutArea.width, this.cutoutArea.height)
    renderer.setViewport(this.cutoutArea.x, h - this.cutoutArea.height - this.cutoutArea.y, this.cutoutArea.width, this.cutoutArea.height)
    renderer.setClearColor(this.cutoutBackground ?? 0x000000, this.cutoutBackgroundAlpha)

    if (this.cutoutCamera) {
      if (this.context.frameSubComposerFrameRender.size > 0)
        this.context.frameSubComposerFrameRender.forEach((cb) => { cb() })
      else
        renderer.render(this.scene!, this.cutoutCamera)
    }
  }

  private initCssRenderer(renderType: CssRendererType = 'base') {
    const rendererOps = this.opts.rendererOps || {}
    const w = rendererOps.size?.width ?? window.innerWidth
    const h = rendererOps.size?.height ?? window.innerHeight
    const cssRenderer = new Cssrenderer(renderType)
    // 初始化即设置尺寸，避免首次渲染时 domElement 宽高为 0
    cssRenderer.setSize(w, h)
    return cssRenderer
  }

  /**
   * init skybox
   * @param imgs
   */
  private initSkyBox(imgs: Tuple<string>) {
    const cubeTextureLoader = new CubeTextureLoader()

    cubeTextureLoader.load([
      imgs[0],
      imgs[1],
      imgs[2],
      imgs[3],
      imgs[4],
      imgs[5],
    ], (texture) => {
      this.scene!.background = texture
    })
  }

  /**
   * init default camera
   */
  private initDefaultPerspectiveCamera() {
    const rendererOps = this.opts.defCameraOps || {}
    const aspect = rendererOps.aspect || window.innerWidth / window.innerHeight

    const camera = new PerspectiveCamera(
      rendererOps.fov || 90,
      aspect,
      rendererOps.near || 0.1,
      rendererOps.far || 5000,
    )

    const position = rendererOps.position || new Vector3(0, 10, 10)
    camera.position.set(position.x, position.y, position.z)

    return camera
  }

  /**
   * init ambient light
   * @param position
   * @param color
   * @param intensity
   * @returns
   */
  private initAmbientLight() {
    const defConfigOps = this.opts.defAmbientLightOps || {}
    const ambientLight = new AmbientLight(defConfigOps.color || 0xFFFFFF, defConfigOps.intensity || 0.5)

    return ambientLight
  }

  /**
   * frame render
   * @param frameAnimate
   */
  private startFrameAnimate(frameAnimate?: (renderer: WebGLRenderer) => void) {
    if (!this.renderer || !this.scene || !this.camera)
      throw new Error('scene or camera or renderer is not init')

    this.context.globalControl.update()
    TWEEN.update()
    const delta = this.clock.getDelta()

    if (this.context.frameCallbacks.size > 0) {
      this.context.frameCallbacks.forEach((cb) => {
        cb(delta)
      })
    }

    if (this.context.frameCallbacks.size > 0) {
      this.context.frameCallbacks.forEach((cb) => {
        cb(delta)
      })
    }

    if (this.interactionManager) {
      const enableEvents = this.context.storeManagement.get('enableEvents') ?? true
      if (enableEvents)
        this.interactionManager.update()
    }

    this.renderer!.setRenderTarget(null)
    // @ts-ignore
    this.controls && !this.controls.isClose && this.controls.update()

    // if need cutout
    if (this.cutout) {
      this.updateRenderForCut()
    }
    else {
      // todo 历史代码 考虑移除
      if (this.context.frameComposerFrameRender.size > 0)
        this.context.frameComposerFrameRender.forEach((cb) => { cb() })
      else
        !this._postProcess && this.renderer!.render(this.scene!, this.camera!)
    }

    if (frameAnimate)
      frameAnimate(this.renderer)

    this.css3DRenderer && this.css3DRenderer.render(this.scene!, this.camera!)
    this.cssRenderer && this.cssRenderer.render(this.scene!, this.camera!)

    if (this.context.frameCallbacksAfterRender.size > 0)
      this.context.frameCallbacksAfterRender.forEach((cb) => { cb(delta) })

    requestAnimationFrame(() => this.startFrameAnimate(frameAnimate))
  }

  /**
   * scene add object3d
   * @param object3d
   */
  public add(object3d: any) {
    this.scene!.add(object3d)
  }

  /**
   * 处理对象添加逻辑
   * @param object3d
   */
  private handleAdd(object3d: any) {
    if (object3d.traverse && this.interactionManager) {
      // 为对象树设置场景实例引用
      object3d.traverse((child: any) => {
        child._sceneInstance = this

        if (child.userData && child.userData.eventHashSet && child.userData.eventHashSet.size > 0) {
          child.userData.eventHashSet.forEach((type: string) => {
            this.interactionManager!.registerEventType(type)
          })
          // 一次性添加所有事件类型
          this.interactionManager!.add(child, Array.from(child.userData.eventHashSet))
        }
      })
    }
  }

  public remove(object3d: any) {
    this.scene!.remove(object3d)
  }

  /**
   * 处理对象移除逻辑
   * @param object3d
   */
  private handleRemove(object3d: any) {
    if (object3d.traverse && this.interactionManager) {
      object3d.traverse((child: any) => {
        // 清理场景引用，在 MaterialSwitchSystem 中使用会存在问题。会意外触发一次删除
        // delete child._sceneInstance

        if (child.userData && child.userData.eventHashSet && child.userData.eventHashSet.size > 0) {
          // 从 interactionManager 移除所有事件
          child.userData.eventHashSet.forEach((type: string) => {
            this.interactionManager!.remove(child)
          })
        }
      })
    }
  }

  /**
   * add pass
   * @param pass
   * @returns
   */
  public addPass(pass: EffectPass) {
    return this.postProcessManager?.addPass(pass)
  }

  /**
   * remove pass
   * @param pass
   * @returns
   */
  public removePass(pass: EffectPass) {
    return this.postProcessManager?.removePass(pass)
  }

  /**
   * get active passes
   * @returns
   */
  public getActivePasses() {
    return this.postProcessManager?.getActivePasses()
  }

  /**
   * render scene
   * @param target
   */
  public render(target: HTMLElement) {
    this.container = target
    this.context.emitter.emit('before-render')

    const renderer = this.initRenderer()
    this.renderer = renderer
    this.context.setRenderer(renderer)

    this.interactionManager = new InteractionManager(renderer, this.camera, renderer.domElement)

    /**
     * 补偿扫描：注册 render() 调用前已添加到场景的带事件对象。
     *
     * interactionManager 在 render() 中才创建，但用户可能在 render()
     * 之前通过 scene.add() 添加了带事件的对象。那些对象的 eventHashSet
     * 已记录了事件类型，但因为当时 interactionManager 为 null，
     * 注册被跳过了。这里统一补上遗漏的注册，同时为所有子对象设置 _sceneInstance，
     * 确保后续 addEventListener 和子树 add 能正常工作。
     */
    this.scene.traverse((child: any) => {
      child._sceneInstance = this
      if (child.userData?.eventHashSet?.size > 0) {
        child.userData.eventHashSet.forEach((type: string) => {
          this.interactionManager!.registerEventType(type)
        })
        this.interactionManager!.add(child, Array.from(child.userData.eventHashSet))
      }
    })

    const currentControlDom: Array<HTMLElement> = []
    const domElement = this.renderer!.domElement

    this.domElement = domElement

    if (this.cssRenderer)
      currentControlDom.push(this.cssRenderer.cssRenderer.domElement)
    if (this.css3DRenderer)
      currentControlDom.push(this.css3DRenderer.cssRenderer.domElement)

    const w = this.opts.rendererOps?.size?.width ?? this.container.offsetWidth
    const h = this.opts.rendererOps?.size?.height ?? this.container.offsetHeight
    currentControlDom.forEach((dom) => {
      target.appendChild(dom)
      dom.style.position = 'absolute'
      dom.style.top = '0'
      dom.style.width = `${w}px`
      dom.style.height = `${h}px`
      dom.style.pointerEvents = 'none'
      dom.style.zIndex = '10'
      dom.style.left = '0'
    })

    this.initControls(domElement)
    this.initEnvironment(renderer)

    this.context.setDomElement(domElement)

    target.appendChild(domElement)
    this.resetScene(this.camera!, this.renderer!)

    const resizeObserver = new ResizeObserver((entries) => {
      this.resetScene(this.camera!, this.renderer!)
    })
    resizeObserver.observe(this.container)
    this.context.emitter.emit('after-render')

    this.startFrameAnimate()
  }

  /**
   * init controls
   */
  private initControls(domElement: HTMLElement) {
    if (!this.opts.orbitControls || (this.opts.orbitControls && this.opts.orbitControls.use !== false)) {
      const controls = new UniversalControls(
        {
          camera: this.camera!,
          domElement,
          scene: this.scene!,
          mode: this.opts.orbitControls?.mode?.type || EControlMode.Orbit,
          characterModelUrl: this.opts.orbitControls?.characterModelUrl,
          spawnPoint: this.opts.orbitControls?.spawnPoint || {
            position: new Vector3(50, 50, 50),
            rotation: new Euler(0, Math.PI / 4, 0),
          },
          limit: this.opts.orbitControls?.limit,
          sceneControl: this,
          target: this.opts.orbitControls?.target,
        },
      )
      this.context.setUniversalControls(controls)
      this.controls = controls.activeControl
      this.universalControls = controls
    }
  }

  // 默认的环境贴图
  private initEnvironment(renderer: WebGLRenderer) {
    // const pmremGenerator = new PMREMGenerator(renderer)
    // pmremGenerator.compileEquirectangularShader()
    // this.scene.environment = pmremGenerator.fromScene(new RoomEnvironment()).texture
    // this.scene.environmentIntensity = 0.5
    const envManager = new EnvManager(this.scene)
    envManager.initDefault(renderer, 0.5)
    this.context.setEnvManager(envManager)
    // globalObjectManage.setEnvManager(envManager)
  }

  private updateRenderForCut() {
    this.renderer!.setScissorTest(true)

    this.cutMain(this.renderer!)
    this.cutSub(this.renderer!)
  }

  /**
   * todo： Not implemented temporarily
   */
  public destroy() {
    const _destroy = (object: any) => {
      while (object.children.length > 0) {
        const child = object.children[0]
        if (child.children && child.children.length > 0)
          _destroy(child)
        if (child.geometry)
          child.geometry.dispose()
        if (child.material) {
          if (Array.isArray(child.material))
            child.material.forEach((material: { dispose: () => any }) => material.dispose())
          else
            child.material.dispose()
        }
        if (child.texture)
          child.texture.dispose()
        object.remove(child)
      }
    }
    // globalObjectManage.frameCallbacks?.clear()
    // globalObjectManage.frameCallbacksAfterRender?.clear()
    // globalObjectManage.frameComposerFrameRender?.clear()
    _destroy(this.scene)
    if (this.scene.background && this.scene.background instanceof Texture)
      this.scene.background.dispose()
    if (this.scene.environment && this.scene.environment instanceof Texture)
      this.scene.environment.dispose()
    this.renderer!.dispose()
  }

  public setCustomPerspective(camera: Camera) {
    const perspectiveBuffer = new WebGLRenderTarget(1000, 1000)
    this.perspectiveMap.set(camera, perspectiveBuffer)

    const close = use.useframe(this, () => {
      this.renderer.setRenderTarget(perspectiveBuffer)
      this.renderer.render(this.scene!, camera)
      this.renderer.setRenderTarget(null)
    })

    return close
  }

  private registerLoadingEvents() {
    DefaultLoadingManager.onLoad = (...args) => {
      this.context.emitter.emit(SceneEventName.LoadingEnd, {
        ...args,
      })
    }
    DefaultLoadingManager.onProgress = (...args) => {
      this.context.emitter.emit(SceneEventName.LoadingProgress, {
        ...args,
      })
    }
    DefaultLoadingManager.onError = (...args) => {
      this.context.emitter.emit(SceneEventName.LoadingError, {
        ...args,
      })
    }
  }

  public addEventListener(event: string, callback: (args: any) => void) {
    if (!this.context.eventCallbacks.has(event))
      this.context.eventCallbacks.set(event, new Set())
    this.context.eventCallbacks.set(event, this.context.eventCallbacks.get(event)!.add(callback))
  }

  public removeEventListener(event: string, callback: (args: any) => void) {
    if (this.context.eventCallbacks.has(event))
      this.context.eventCallbacks.get(event)!.delete(callback)

    removeCurrentEvent(event, this.context)
  }

  public dispatchEvent(opts: { type: string;args?: any }) {
    const { type, args } = opts

    if (this.context.eventCallbacks.has(type)) {
      this.context.eventCallbacks.get(type)!.forEach((cb) => {
        cb(args)
      })
    }
  }
}

export default SceneControl
